import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import {
  AlertTriangle,
  Ban,
  Building2,
  Check,
  ChevronDown,
  ExternalLink,
  Link2,
  Loader2,
  Pencil,
  Radar,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { EmptyState, TableShell, Th } from "@/features/power_projects/components/ui";
import { usePageTitle } from "@/hooks/usePageTitle";
import { CandidateEditModal } from "@/features/leads/components/CandidateEditModal";
import {
  useAcceptCandidates,
  useEnrichCandidates,
  useLeadSearch,
  useRefineSearch,
  useRejectCandidates,
  useRerunSearch,
  type AcceptOutcome,
  type SearchCandidate,
} from "@/features/leads/api/searches";

/**
 * Kandydaci z jednego wyszukiwania.
 *
 * Kolizje są widoczne PRZY kandydacie, a nie dopiero po kliknięciu: firma już w
 * bazie i firma z listy „nie kontaktować" mają odznaczony checkbox i nie da się
 * ich zaznaczyć hurtem. Istniejący podwykonawca to tylko ostrzeżenie — bywa powód,
 * żeby mieć go też jako leada.
 */

function errorText(err: unknown): string {
  // ValidationError("tekst") przychodzi jako lista, błędy pól — jako słownik.
  const errors = (err as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors;
  const first = Array.isArray(errors)
    ? errors[0]
    : errors && typeof errors === "object"
      ? Object.values(errors as Record<string, unknown>)[0]
      : errors;
  if (Array.isArray(first)) return String(first[0]);
  if (typeof first === "string") return first;
  return "Nie udało się wysłać dopytania.";
}

export function LeadSearchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: search, isLoading } = useLeadSearch(id);
  const accept = useAcceptCandidates();
  const reject = useRejectCandidates();
  const refine = useRefineSearch();
  const rerun = useRerunSearch();
  const enrich = useEnrichCandidates();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [outcome, setOutcome] = useState<AcceptOutcome | null>(null);
  const [editing, setEditing] = useState<SearchCandidate | null>(null);
  const [refinePrompt, setRefinePrompt] = useState("");
  const [showLog, setShowLog] = useState(false);

  usePageTitle("Leady — wyniki wyszukiwania");

  const proposed = useMemo(
    () => (search?.candidates ?? []).filter((row) => row.status === "proposed"),
    [search],
  );
  const selectable = proposed.filter((row) => !row.is_blocked);

  if (isLoading) return <PageLoader />;
  if (!search) {
    return <p className="section-label">Nie znaleziono wyszukiwania.</p>;
  }

  const running = search.status === "pending" || search.status === "running";
  const rounds = search.rounds ?? [];
  const latestRound = rounds.length;
  const roundCounts = (search.candidates ?? []).reduce<Record<number, number>>((acc, row) => {
    acc[row.round ?? 0] = (acc[row.round ?? 0] ?? 0) + 1;
    return acc;
  }, {});
  const canRefine = !running && refinePrompt.trim().length >= 5;
  const submitRefine = () =>
    refine.mutate(
      { id: search.id, prompt: refinePrompt.trim() },
      { onSuccess: () => setRefinePrompt("") },
    );

  const toggle = (candidateId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(candidateId)) next.delete(candidateId);
      else next.add(candidateId);
      return next;
    });

  const decide = (kind: "accept" | "reject") => {
    const resultIds = [...selected];
    if (resultIds.length === 0) return;
    const mutation = kind === "accept" ? accept : reject;
    mutation.mutate(
      { searchId: search.id, resultIds },
      {
        onSuccess: (data) => {
          setSelected(new Set());
          setOutcome(kind === "accept" ? (data as AcceptOutcome) : null);
        },
      },
    );
  };

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to="/leads/search" className="section-label hover:text-[var(--color-primary)]">
            ← Wyszukiwania
          </Link>
          <p className="mt-1 max-w-3xl text-sm text-[var(--color-text-primary)]">{search.prompt}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            loading={rerun.isPending}
            disabled={running}
            title="Pyta jeszcze raz tym samym promptem; nieprzejrzani kandydaci znikają"
            onClick={() => {
              if (!confirm("Powtórzyć wyszukiwanie? Nieprzejrzani kandydaci zostaną zastąpieni.")) return;
              rerun.mutate(search.id);
            }}
          >
            <RefreshCw size={13} className="mr-1.5 inline" /> Powtórz
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={selected.size === 0}
            loading={enrich.isPending}
            title="Dla zaznaczonych firm: NIP z rejestru, mail i telefon ze strony, osoba z KRS"
            onClick={() =>
              enrich.mutate(
                { searchId: search.id, resultIds: [...selected] },
                { onSuccess: () => setSelected(new Set()) },
              )
            }
          >
            <Radar size={13} className="mr-1.5 inline" /> Doszukaj dane ({selected.size})
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={selected.size === 0}
            loading={reject.isPending}
            onClick={() => decide("reject")}
          >
            Odrzuć ({selected.size})
          </Button>
          <Button
            size="sm"
            disabled={selected.size === 0}
            loading={accept.isPending}
            onClick={() => decide("accept")}
          >
            Dodaj do bazy ({selected.size})
          </Button>
        </div>
      </div>

      <div className="card mb-5 flex flex-wrap gap-x-6 gap-y-1 p-3 text-xs text-[var(--color-text-secondary)]">
        <span>
          Status:{" "}
          <span className="font-mono">
            {running && <Loader2 size={10} className="mr-1 inline animate-spin" />}
            {search.status_label}
          </span>
        </span>
        <span>
          Kandydaci: <span className="font-mono">{search.counts.total}</span> · do przejrzenia{" "}
          <span className="font-mono">{search.counts.proposed}</span> · dodanych{" "}
          <span className="font-mono">{search.counts.accepted}</span>
        </span>
        {Number(search.cost_usd) > 0 && (
          <span>
            Koszt: <span className="font-mono">${Number(search.cost_usd).toFixed(2)}</span> (
            {search.web_searches} zapytań, {search.model_used})
          </span>
        )}
        {search.finished_at && (
          <span>Zakończono: {format(new Date(search.finished_at), "dd.MM.yyyy HH:mm")}</span>
        )}
      </div>

      {search.log && (
        <div className="card mb-4">
          <button
            type="button"
            onClick={() => setShowLog((value) => !value)}
            className="flex w-full items-center justify-between px-3 py-2 text-left"
          >
            <span className="section-label">
              Tok pracy modelu {running && <span className="text-[var(--color-primary)]">— trwa</span>}
            </span>
            <ChevronDown
              size={14}
              className={showLog ? "rotate-180 transition-transform" : "transition-transform"}
            />
          </button>
          {showLog && (
            <pre className="max-h-80 overflow-y-auto whitespace-pre-wrap border-t border-[var(--color-border)] p-3 text-[11px] leading-relaxed text-[var(--color-text-secondary)]">
              {search.log}
            </pre>
          )}
        </div>
      )}

      {search.error && (
        <p className="mb-4 flex items-start gap-2 border-l-2 border-[var(--color-primary)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-primary)]">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          {search.error}
        </p>
      )}

      {outcome && (
        <div className="card mb-4 p-3 text-sm">
          <p className="text-[var(--color-text-primary)]">
            Dodano do bazy: <span className="font-mono">{outcome.created.length}</span>
          </p>
          {outcome.skipped.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-xs text-[var(--color-text-secondary)]">
              {outcome.skipped.map((row) => (
                <li key={row.id}>
                  <span className="text-[var(--color-primary)]">pominięto</span> {row.name} —{" "}
                  {row.reason}
                </li>
              ))}
            </ul>
          )}
          {/* Firma bez adresu wchodzi do bazy, ale kampania ją pominie — lepiej
              powiedzieć to teraz niż przy pustej liście odbiorców. */}
          {outcome.created.some((row) => !row.email) && (
            <ul className="mt-1 space-y-0.5 text-xs text-[var(--color-text-secondary)]">
              {outcome.created
                .filter((row) => !row.email)
                .map((row) => (
                  <li key={row.id}>
                    <span className="text-[var(--color-primary)]">bez adresu</span>{" "}
                    <Link to={`/leads/${row.id}`} className="hover:underline">
                      {row.name}
                    </Link>{" "}
                    — {row.warning ?? "kampania pominie tę firmę; dodaj osobę kontaktową."}
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}

      {(search.parent_prompt || search.refinements.length > 0) && (
        <div className="card mb-4 p-3 text-xs">
          {search.parent_prompt && (
            <p className="text-[var(--color-text-secondary)]">
              Dopytanie do:{" "}
              <Link to={`/leads/search/${search.parent}`} className="hover:text-[var(--color-primary)]">
                „{search.parent_prompt}"
              </Link>
            </p>
          )}
          {search.refinements.length > 0 && (
            <div className="mt-1">
              <span className="section-label">Dopytania</span>
              <ul className="mt-1 space-y-0.5">
                {search.refinements.map((child) => (
                  <li key={child.id}>
                    <Link
                      to={`/leads/search/${child.id}`}
                      className="text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
                    >
                      „{child.prompt}" — {child.count} firm ({child.status})
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {rounds.length > 0 && (
        <div className="card mb-4 p-3 text-xs">
          <span className="section-label">Pytania w tym zleceniu</span>
          <ol className="mt-1 space-y-0.5 text-[var(--color-text-secondary)]">
            <li>
              <span className="mr-1.5 font-mono text-[var(--color-text-muted)]">1.</span>„{search.prompt}"
              {" — "}
              <span className="font-mono">{roundCounts[0] ?? 0}</span> firm
            </li>
            {rounds.map((round, index) => {
              const pending = !round.finished_at;
              return (
                <li key={`${round.asked_at}-${index}`}>
                  <span className="mr-1.5 font-mono text-[var(--color-text-muted)]">{index + 2}.</span>„
                  {round.prompt}"{" — "}
                  {pending ? (
                    <span className="text-[var(--color-primary)]">
                      <Loader2 size={10} className="mr-1 inline animate-spin" />
                      szukam…
                    </span>
                  ) : round.error ? (
                    <span className="text-[var(--color-primary)]">nie wyszło</span>
                  ) : (
                    <>
                      <span className="font-mono">{roundCounts[index + 1] ?? round.found ?? 0}</span> firm
                      {Number(round.cost_usd) > 0 && (
                        <span className="text-[var(--color-text-muted)]">
                          {" "}
                          · ${Number(round.cost_usd).toFixed(2)}
                        </span>
                      )}
                    </>
                  )}
                  {round.asked_by && (
                    <span className="text-[var(--color-text-muted)]"> · {round.asked_by}</span>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      <div className="card mb-5 p-3">
        <span className="section-label">Dopytaj</span>
        <p className="mb-2 mt-1 text-xs text-[var(--color-text-muted)]">
          Kolejne pytanie w tym samym zleceniu. Model dostanie listę firm, które już znamy,
          oraz Twoje decyzje — czego szukać podobnego (dodane) i czego unikać (odrzucone).
          Nowe firmy dopiszą się do listy poniżej; dotychczasowe zostają.
        </p>
        <div className="mb-2 flex flex-wrap gap-1.5">
          {[
            "szukaj szerzej, także w sąsiednich powiatach",
            "tylko firmy z własną stroną i mailem",
            "znajdź mniejsze, jednoosobowe działalności",
            "pomiń pośredników i katalogi, szukaj wykonawców",
          ].map((hint) => (
            <button
              key={hint}
              type="button"
              onClick={() => setRefinePrompt(hint)}
              className="border border-[var(--color-border)] px-2 py-1 text-[11px] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
            >
              {hint}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="input-base min-w-[18rem] flex-1"
            placeholder="Np. teraz tylko firmy z uprawnieniami do obsługi kolei, powiat obok"
            value={refinePrompt}
            onChange={(e) => setRefinePrompt(e.target.value)}
            disabled={running}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canRefine) submitRefine();
            }}
          />
          <Button size="sm" loading={refine.isPending} disabled={!canRefine} onClick={submitRefine}>
            <Sparkles size={13} className="mr-1.5 inline" /> Szukaj dalej
          </Button>
        </div>
        {refine.isError && (
          <p className="mt-2 text-xs text-[var(--color-primary)]">{errorText(refine.error)}</p>
        )}
      </div>

      {running && search.counts.total === 0 ? (
        <div className="card">
          <EmptyState>Model szuka firm… Wyniki pojawią się tutaj same.</EmptyState>
        </div>
      ) : (search.candidates ?? []).length === 0 ? (
        <div className="card">
          <EmptyState>Wyszukiwanie nie zwróciło żadnej firmy.</EmptyState>
        </div>
      ) : (
        <TableShell tableClassName="table-fixed">
          <thead>
            <tr>
              <Th className="w-10">
                <input
                  type="checkbox"
                  title="Zaznacz wszystkich, których da się dodać"
                  checked={selectable.length > 0 && selected.size === selectable.length}
                  onChange={(e) =>
                    setSelected(e.target.checked ? new Set(selectable.map((r) => r.id)) : new Set())
                  }
                />
              </Th>
              <Th className="w-[26%]">Firma</Th>
              <Th className="w-[18%]">Kontakt</Th>
              <Th className="w-[38%]">Dlaczego pasuje</Th>
              <Th className="w-[18%]">Uwagi</Th>
              <Th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {(search.candidates ?? []).map((row) => (
              <CandidateRow
                key={row.id}
                candidate={row}
                isNew={latestRound > 0 && row.round === latestRound}
                checked={selected.has(row.id)}
                onToggle={() => toggle(row.id)}
                onEdit={() => setEditing(row)}
              />
            ))}
          </tbody>
        </TableShell>
      )}

      <CandidateEditModal candidate={editing} onClose={() => setEditing(null)} />
    </>
  );
}

/**
 * Skąd jest ta dana.
 *
 * Rejestr i strona firmy to dwie różne klasy pewności: adres z białej listy albo
 * KRS jest urzędowy, adres z katalogu bywa sprzed pięciu lat. Bez tego znacznika
 * jedno i drugie wygląda w tabeli identycznie.
 */
function Provenance({
  source,
}: {
  source?: { origin: "registry" | "krs" | "web"; label: string; url?: string };
}) {
  if (!source) return null;
  const official = source.origin !== "web";
  const className = official
    ? "ml-1 inline-flex items-center gap-0.5 text-[10px] text-emerald-600"
    : "ml-1 inline-flex items-center gap-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-primary)]";
  const label = (
    <>
      {official ? <ShieldCheck size={9} /> : <Link2 size={9} />}
      {source.label}
    </>
  );
  return source.url ? (
    <a href={source.url} target="_blank" rel="noreferrer" title={source.url} className={className}>
      {label}
    </a>
  ) : (
    <span className={className} title={source.label}>
      {label}
    </span>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url.slice(0, 24);
  }
}

function CandidateRow({
  candidate,
  isNew,
  checked,
  onToggle,
  onEdit,
}: {
  candidate: SearchCandidate;
  /** Z ostatniego dopytania — żeby po „szukaj dalej" było widać, co doszło. */
  isNew: boolean;
  checked: boolean;
  onToggle: () => void;
  onEdit: () => void;
}) {
  const decided = candidate.status !== "proposed";
  return (
    <tr
      className={
        decided
          ? "border-t border-[var(--color-border)] align-top opacity-60"
          : "border-t border-[var(--color-border)] align-top"
      }
    >
      <td className="px-3 py-2.5">
        {!decided && (
          <input
            type="checkbox"
            checked={checked}
            disabled={candidate.is_blocked}
            onChange={onToggle}
            title={candidate.is_blocked ? "Tej firmy nie da się dodać" : undefined}
          />
        )}
      </td>
      <td className="px-3 py-2.5">
        <button
          type="button"
          onClick={onEdit}
          className="block w-full break-words text-left text-sm font-medium leading-snug text-[var(--color-text-primary)] hover:text-[var(--color-primary)]"
          title="Podgląd i poprawki"
        >
          {candidate.name}
        </button>
        {isNew && (
          <Badge color="blue" className="mt-1">
            z dopytania {candidate.round}
          </Badge>
        )}
        <div className="mt-1 font-mono text-[11px] leading-relaxed text-[var(--color-text-muted)]">
          <span className={candidate.nip_verified ? "whitespace-nowrap text-emerald-600" : "whitespace-nowrap"}>
            {candidate.nip_verified && <ShieldCheck size={10} className="mr-1 inline" />}
            {candidate.nip ? `NIP ${candidate.nip}` : "bez NIP"}
          </span>
          {candidate.field_sources?.nip && <Provenance source={candidate.field_sources.nip} />}
          {candidate.city && <div className="break-words">{candidate.city}</div>}
        </div>
        {candidate.website && (
          <a
            href={candidate.website.startsWith("http") ? candidate.website : `https://${candidate.website}`}
            target="_blank"
            rel="noreferrer"
            className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-[var(--color-primary)] hover:underline"
          >
            <ExternalLink size={10} /> strona firmy
          </a>
        )}
      </td>
      <td className="px-3 py-2.5 text-[11px] leading-relaxed text-[var(--color-text-secondary)]">
        {candidate.contact_person && (
          <div className="break-words">
            {candidate.contact_person}
            <Provenance source={candidate.field_sources?.contact_person} />
          </div>
        )}
        {candidate.email && (
          <div className="break-all">
            {candidate.email}
            <Provenance source={candidate.field_sources?.email} />
          </div>
        )}
        {candidate.phone && (
          <div className="font-mono">
            {candidate.phone}
            <Provenance source={candidate.field_sources?.phone} />
          </div>
        )}
        {!candidate.contact_person && !candidate.email && !candidate.phone && (
          <span className="text-[var(--color-text-muted)]">brak danych</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-[11px] leading-relaxed text-[var(--color-text-secondary)]">
        <p className="break-words">{candidate.rationale || "—"}</p>
        {candidate.sources.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {candidate.sources.map((url) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noreferrer"
                title={url}
                className="border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
              >
                {hostOf(url)}
              </a>
            ))}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5 text-[11px]">
        {decided && (
          <Badge color={candidate.status === "accepted" ? "green" : "gray"}>
            {candidate.status_label}
          </Badge>
        )}
        {candidate.lead && (
          <Link
            to={`/leads/${candidate.lead}`}
            className="ml-1 text-[var(--color-primary)] hover:underline"
          >
            otwórz
          </Link>
        )}
        {candidate.existing_lead_name && (
          <div className="mt-1 flex items-start gap-1 text-[var(--color-primary)]">
            <AlertTriangle size={11} className="mt-0.5 shrink-0" />
            <span>
              już w bazie:{" "}
              <Link to={`/leads/${candidate.existing_lead}`} className="underline">
                {candidate.existing_lead_name}
              </Link>
            </span>
          </div>
        )}
        {candidate.existing_contractor_name && (
          <div className="mt-1 flex items-start gap-1 text-amber-600 dark:text-amber-500">
            <Building2 size={11} className="mt-0.5 shrink-0" />
            <span>już współpracuje: {candidate.existing_contractor_name}</span>
          </div>
        )}
        {candidate.is_suppressed && (
          <div className="mt-1 flex items-start gap-1 text-[var(--color-primary)]">
            <Ban size={11} className="mt-0.5 shrink-0" />
            <span>na liście „nie kontaktować”</span>
          </div>
        )}
        {!decided &&
          !candidate.existing_lead_name &&
          !candidate.existing_contractor_name &&
          !candidate.is_suppressed && (
            <span className="inline-flex items-center gap-1 text-[var(--color-text-muted)]">
              <Check size={11} /> nowa firma
            </span>
          )}
        {candidate.enrichment_status === "pending" && (
          <div className="mt-1 text-[var(--color-text-muted)]">doszukiwanie w kolejce…</div>
        )}
        {candidate.enrichment_status === "running" && (
          <div className="mt-1 flex items-center gap-1 text-blue-600">
            <Loader2 size={10} className="animate-spin" /> doszukuję danych…
          </div>
        )}
        {candidate.enrichment_status === "done" && (
          <div className="mt-1 text-emerald-600" title={candidate.enrichment_log}>
            <Radar size={10} className="mr-1 inline" /> doszukane
          </div>
        )}
        {candidate.enrichment_status === "failed" && (
          <div className="mt-1 text-[var(--color-primary)]" title={candidate.enrichment_log}>
            doszukiwanie nie wyszło
          </div>
        )}
      </td>
      <td className="px-3 py-2.5 text-right">
        <button
          type="button"
          onClick={onEdit}
          title="Popraw dane kandydata"
          className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
        >
          <Pencil size={13} />
        </button>
      </td>
    </tr>
  );
}
